import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchCourseCurriculumByCourseId } from '@/redux/actions/courseCurriculum';
import VideoPlayer from '@/components/VideoPlayer';
import { TopicVideoPlayer } from '@/components/TopicVideoPlayer';
import CourseResourcesList from '@/components/CourseResourcesList';
import { TopicReviewModal } from '@/components/modals/TopicReviewModal';
import type {
  CourseCurriculumModule,
  CourseCurriculumLesson,
  CourseCurriculumSegment,
  CourseCurriculumTopic
} from '@/types/courseCurriculum.types';
import { trackClassroom, trackPageView } from '@/services/analytics';
import { progressService } from '@/services/progress.service';
import { enrollmentService } from '@/services/enrollment.service';
import { topicReviewService } from '@/services/topicReview.service';

// Utility functions for cascading video availability check
const hasTopicVideo = (topic: CourseCurriculumTopic): boolean => {
  return !!(topic.video_exists || topic.video_url);
};

const hasSegmentVideos = (segment: CourseCurriculumSegment): boolean => {
  return segment.topics?.some(hasTopicVideo) ?? false;
};

const hasLessonVideos = (lesson: CourseCurriculumLesson): boolean => {
  return lesson.segments?.some(hasSegmentVideos) ?? false;
};

const hasModuleVideos = (module: CourseCurriculumModule): boolean => {
  return module.lessons?.some(hasLessonVideos) ?? false;
};

// Update In Progress Badge component
const UpdateInProgressBadge: React.FC = () => (
  <span className="ml-2 px-1.5 py-0.5 text-[10px] font-medium bg-gray-600/40 text-gray-400 rounded border border-gray-500/40 whitespace-nowrap uppercase tracking-wide">
    Update in Progress
  </span>
);
import { useScrollTracking } from '@/hooks/useAnalytics';

const Classroom: React.FC = () => {
  // Add custom scrollbar styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .custom-scrollbar::-webkit-scrollbar {
        width: 10px;
        height: 10px;
      }
      .custom-scrollbar::-webkit-scrollbar-track {
        background: rgba(30, 41, 59, 0.4);
        border-radius: 10px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background: linear-gradient(180deg, rgba(79, 70, 229, 0.6) 0%, rgba(124, 58, 237, 0.6) 100%);
        border-radius: 10px;
        border: 2px solid rgba(30, 41, 59, 0.4);
      }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: linear-gradient(180deg, rgba(79, 70, 229, 0.8) 0%, rgba(124, 58, 237, 0.8) 100%);
      }
      .custom-scrollbar {
        scrollbar-width: thin;
        scrollbar-color: rgba(79, 70, 229, 0.6) rgba(30, 41, 59, 0.4);
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux selectors
  const curriculum = useSelector((state: any) =>
    state.courseCurriculum.curriculums[courseId || '']
  );
  const curriculumLoading = useSelector((state: any) =>
    state.courseCurriculum.loading
  );
  const curriculumError = useSelector((state: any) =>
    state.courseCurriculum.error
  );

  // Local state
  const [selectedModule, setSelectedModule] = useState<number>(0);
  const [selectedLesson, setSelectedLesson] = useState<number>(0);
  const [selectedSegment, setSelectedSegment] = useState<number>(0);
  const [selectedTopic, setSelectedTopic] = useState<number>(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Progress tracking state
  const [enrollmentId, setEnrollmentId] = useState<string | null>(null);
  const [completedTopicIds, setCompletedTopicIds] = useState<Set<string>>(new Set());
  const [overallProgress, setOverallProgress] = useState<number>(0);
  const [progressLoading, setProgressLoading] = useState(true);

  // Review modal state
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewedTopicIds, setReviewedTopicIds] = useState<Set<string>>(new Set());
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

  // Video player expanded state (collapsed by default when sidebar is open)
  const [videoPlayerExpanded, setVideoPlayerExpanded] = useState(false);

  // Content area ref for auto-scrolling
  const contentAreaRef = React.useRef<HTMLDivElement>(null);

  // Track scroll depth
  useScrollTracking('Classroom');

  // Fetch curriculum on mount
  useEffect(() => {
    if (courseId) {
      dispatch(fetchCourseCurriculumByCourseId(courseId) as any);
    }
  }, [courseId, dispatch]);

  // Track classroom entry when curriculum loads
  useEffect(() => {
    if (curriculum && courseId) {
      trackClassroom.enter(courseId, curriculum.course?.title || 'Unknown Course');
      trackPageView('Classroom', { courseId, courseName: curriculum.course?.title });
    }
  }, [curriculum, courseId]);

  // Fetch enrollment and progress data
  useEffect(() => {
    const fetchProgressData = async () => {
      if (!courseId) return;
      setProgressLoading(true);
      try {
        // First, get the user's enrollment for this course
        const enrollmentsResult = await enrollmentService.getMyEnrollments();
        if (enrollmentsResult.success && enrollmentsResult.data) {
          const enrollment = enrollmentsResult.data.find(
            (e: any) => e.product_id === courseId && e.status === 'active'
          );
          if (enrollment) {
            setEnrollmentId(enrollment.id);
            setOverallProgress(enrollment.progress_percentage || 0);

            // Fetch completed topics for this enrollment
            const progressResult = await progressService.getCourseProgressDetail(enrollment.id);
            if (progressResult.success && progressResult.data) {
              setCompletedTopicIds(new Set(progressResult.data.completed_topic_ids || []));
              setOverallProgress(progressResult.data.overall_progress_percentage || 0);
            }

            // Fetch reviewed topics for this enrollment
            const reviewResult = await topicReviewService.getReviewedTopicIds(enrollment.id);
            if (reviewResult.success && reviewResult.data) {
              setReviewedTopicIds(new Set(reviewResult.data.reviewed_topic_ids || []));
            }
          }
        }
      } catch (err) {
        console.error('[Classroom] Error fetching progress data:', err);
      } finally {
        setProgressLoading(false);
      }
    };
    fetchProgressData();
  }, [courseId]);

  // Reset to first item when curriculum loads
  useEffect(() => {
    if (curriculum) {
      setSelectedModule(0);
      setSelectedLesson(0);
      setSelectedSegment(0);
      setSelectedTopic(0);
    }
  }, [curriculum]);

  // Get current items
  const currentModule = curriculum?.course?.modules?.[selectedModule];
  const currentLesson = currentModule?.lessons?.[selectedLesson];
  const currentSegment = currentLesson?.segments?.[selectedSegment];
  const currentTopic = currentSegment?.topics?.[selectedTopic];

  // Callback when a topic video is completed
  const handleTopicCompleted = useCallback((topicId: string, newProgressPercentage: number) => {
    setCompletedTopicIds(prev => new Set([...prev, topicId]));
    setOverallProgress(newProgressPercentage);
  }, []);

  // Actual navigation logic (extracted to be called after review)
  const performNavigation = useCallback(() => {
    if (!curriculum?.course?.modules) return;

    const module = curriculum.course.modules[selectedModule];
    const lesson = module.lessons[selectedLesson];
    const segment = lesson.segments[selectedSegment];
    const currentTopicName = segment.topics[selectedTopic]?.content || '';

    // Check if there's a next topic in current segment
    if (selectedTopic < segment.topics.length - 1) {
      const nextTopicName = segment.topics[selectedTopic + 1]?.content || '';
      trackClassroom.navigationNext(currentTopicName, nextTopicName, courseId || '');
      setSelectedTopic(selectedTopic + 1);
    }
    // Check if there's a next segment in current lesson
    else if (selectedSegment < lesson.segments.length - 1) {
      const nextSegment = lesson.segments[selectedSegment + 1];
      const nextTopicName = nextSegment.topics[0]?.content || '';
      trackClassroom.navigationNext(currentTopicName, nextTopicName, courseId || '');
      setSelectedSegment(selectedSegment + 1);
      setSelectedTopic(0);
    }
    // Check if there's a next lesson in current module
    else if (selectedLesson < module.lessons.length - 1) {
      const nextLesson = module.lessons[selectedLesson + 1];
      const nextTopicName = nextLesson.segments[0]?.topics[0]?.content || '';
      trackClassroom.lessonComplete(lesson.id, lesson.title, courseId || '');
      trackClassroom.navigationNext(currentTopicName, nextTopicName, courseId || '');
      setSelectedLesson(selectedLesson + 1);
      setSelectedSegment(0);
      setSelectedTopic(0);
    }
    // Check if there's a next module
    else if (selectedModule < curriculum.course.modules.length - 1) {
      const nextModule = curriculum.course.modules[selectedModule + 1];
      const nextTopicName = nextModule.lessons[0]?.segments[0]?.topics[0]?.content || '';
      trackClassroom.lessonComplete(lesson.id, lesson.title, courseId || '');
      trackClassroom.navigationNext(currentTopicName, nextTopicName, courseId || '');
      setSelectedModule(selectedModule + 1);
      setSelectedLesson(0);
      setSelectedSegment(0);
      setSelectedTopic(0);
    }

    scrollToTop();
  }, [curriculum, selectedModule, selectedLesson, selectedSegment, selectedTopic, courseId]);

  // Handle "Next" button click - show review modal if topic completed but not reviewed
  const goToNextTopic = () => {
    if (!currentTopic || !enrollmentId) {
      performNavigation();
      return;
    }

    const topicId = currentTopic.id;
    const isCompleted = completedTopicIds.has(topicId);
    const hasReview = reviewedTopicIds.has(topicId);
    const hasVideo = hasTopicVideo(currentTopic);

    // Show review modal if:
    // 1. Topic has a video AND
    // 2. Topic is completed AND
    // 3. Topic hasn't been reviewed yet
    if (hasVideo && isCompleted && !hasReview) {
      setPendingNavigation(() => performNavigation);
      setReviewModalOpen(true);
    } else {
      performNavigation();
    }
  };

  // Handle review modal close
  const handleReviewModalClose = () => {
    setReviewModalOpen(false);
    // Execute pending navigation after modal closes
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  };

  // Handle review submitted
  const handleReviewSubmitted = () => {
    if (currentTopic) {
      setReviewedTopicIds(prev => new Set([...prev, currentTopic.id]));
    }
  };

  const goToPreviousTopic = () => {
    if (!curriculum?.course?.modules) return;

    const module = curriculum.course.modules[selectedModule];
    const lesson = module.lessons[selectedLesson];
    const segment = lesson.segments[selectedSegment];
    const currentTopicName = segment.topics[selectedTopic]?.content || '';

    // Check if there's a previous topic in current segment
    if (selectedTopic > 0) {
      const prevTopicName = segment.topics[selectedTopic - 1]?.content || '';
      trackClassroom.navigationPrevious(currentTopicName, prevTopicName, courseId || '');
      setSelectedTopic(selectedTopic - 1);
    }
    // Check if there's a previous segment in current lesson
    else if (selectedSegment > 0) {
      const prevSegment = curriculum.course.modules[selectedModule].lessons[selectedLesson].segments[selectedSegment - 1];
      const prevTopicName = prevSegment.topics[prevSegment.topics.length - 1]?.content || '';
      trackClassroom.navigationPrevious(currentTopicName, prevTopicName, courseId || '');
      setSelectedSegment(selectedSegment - 1);
      setSelectedTopic(prevSegment.topics.length - 1);
    }
    // Check if there's a previous lesson in current module
    else if (selectedLesson > 0) {
      const prevLesson = curriculum.course.modules[selectedModule].lessons[selectedLesson - 1];
      const prevSegment = prevLesson.segments[prevLesson.segments.length - 1];
      const prevTopicName = prevSegment.topics[prevSegment.topics.length - 1]?.content || '';
      trackClassroom.navigationPrevious(currentTopicName, prevTopicName, courseId || '');
      setSelectedLesson(selectedLesson - 1);
      setSelectedSegment(prevLesson.segments.length - 1);
      setSelectedTopic(prevSegment.topics.length - 1);
    }
    // Check if there's a previous module
    else if (selectedModule > 0) {
      const prevModule = curriculum.course.modules[selectedModule - 1];
      const prevLesson = prevModule.lessons[prevModule.lessons.length - 1];
      const prevSegment = prevLesson.segments[prevLesson.segments.length - 1];
      const prevTopicName = prevSegment.topics[prevSegment.topics.length - 1]?.content || '';
      trackClassroom.navigationPrevious(currentTopicName, prevTopicName, courseId || '');
      setSelectedModule(selectedModule - 1);
      setSelectedLesson(prevModule.lessons.length - 1);
      setSelectedSegment(prevLesson.segments.length - 1);
      setSelectedTopic(prevSegment.topics.length - 1);
    }

    // Scroll to top of content area
    scrollToTop();
  };

  // Scroll to top of content area
  const scrollToTop = () => {
    contentAreaRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Handle topic click - scroll to top when selecting a topic
  const handleTopicClick = (index: number) => {
    const topic = currentSegment?.topics[index];
    if (topic && courseId) {
      trackClassroom.topicSelect(index, topic.content, currentLesson?.id || '', courseId);
    }
    setSelectedTopic(index);
    setVideoPlayerExpanded(false); // Collapse video player when changing topics
    scrollToTop();
  };

  // Handle sidebar toggle
  const handleSidebarToggle = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    // Reset video player expanded state when sidebar expands (showing play card)
    if (!newState) {
      setVideoPlayerExpanded(false);
    }
    if (courseId) {
      trackClassroom.sidebarToggle(newState, courseId);
    }
  };

  // Handle module selection with tracking
  const handleModuleSelect = (moduleIndex: number) => {
    const module = curriculum?.course?.modules?.[moduleIndex];
    if (module && courseId) {
      trackClassroom.moduleSelect(moduleIndex, module.title, courseId);
    }
    setSelectedModule(moduleIndex);
    setSelectedLesson(0);
    setSelectedSegment(0);
    setSelectedTopic(0);
  };

  // Handle lesson selection with tracking
  const handleLessonSelect = (moduleIndex: number, lessonIndex: number) => {
    const lesson = curriculum?.course?.modules?.[moduleIndex]?.lessons?.[lessonIndex];
    if (lesson && courseId) {
      trackClassroom.lessonSelect(lessonIndex, lesson.title, curriculum?.course?.modules?.[moduleIndex]?.id || '', courseId);
    }
    setSelectedModule(moduleIndex);
    setSelectedLesson(lessonIndex);
    setSelectedSegment(0);
    setSelectedTopic(0);
  };

  const isFirstTopic = selectedModule === 0 && selectedLesson === 0 && selectedSegment === 0 && selectedTopic === 0;
  const isLastTopic = curriculum &&
    selectedModule === curriculum.course.modules.length - 1 &&
    selectedLesson === curriculum.course.modules[selectedModule].lessons.length - 1 &&
    selectedSegment === curriculum.course.modules[selectedModule].lessons[selectedLesson].segments.length - 1 &&
    selectedTopic === curriculum.course.modules[selectedModule].lessons[selectedLesson].segments[selectedSegment].topics.length - 1;

  // Loading state
  if (curriculumLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-300 text-lg">Loading curriculum...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (curriculumError || !curriculum) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-400 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-white mb-2">Unable to Load Curriculum</h2>
          <p className="text-gray-400 mb-6">{curriculumError || 'Curriculum not found for this course'}</p>
          <button
            onClick={() => navigate('/dashboard/courses')}
            className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-all"
          >
            Back to My Courses
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex overflow-hidden">
      {/* Sidebar - Course Navigation */}
      <div className={`${sidebarCollapsed ? 'w-16' : 'w-80'} bg-slate-800/80 backdrop-blur-sm border-r border-slate-700 flex flex-col transition-all duration-300 overflow-hidden flex-shrink-0`}>
        {/* Header */}
        <div className={`border-b border-slate-700 flex items-center ${sidebarCollapsed ? 'p-2 justify-center' : 'p-4 justify-between'}`}>
          {!sidebarCollapsed && (
            <div className="flex-1 min-w-0 mr-2">
              <h2 className="text-lg font-bold text-white truncate">{curriculum.course.title}</h2>
              <p className="text-xs text-gray-400 mt-1">{curriculum.course.overview.total_modules} modules • {curriculum.course.overview.total_lessons} lessons</p>
            </div>
          )}
          <button
            onClick={handleSidebarToggle}
            className={`rounded-lg transition-all flex-shrink-0 flex items-center justify-center ${
              sidebarCollapsed
                ? 'w-12 h-12 bg-gradient-to-br from-indigo-600/80 to-purple-600/80 hover:from-indigo-500 hover:to-purple-500 shadow-lg shadow-indigo-500/30'
                : 'p-2 hover:bg-slate-700'
            }`}
          >
            <svg className={`transition-transform ${sidebarCollapsed ? 'w-6 h-6 text-white rotate-180' : 'w-5 h-5 text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>
        </div>

        {/* Course Structure Navigation */}
        {!sidebarCollapsed && (
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            {curriculum.course.modules.map((module: CourseCurriculumModule, moduleIndex: number) => {
              const moduleHasVideos = hasModuleVideos(module);
              return (
              <div key={module.id} className="border-b border-slate-700/50">
                <div
                  className={`p-4 cursor-pointer transition-colors ${
                    moduleIndex === selectedModule
                      ? 'bg-indigo-600/20 border-l-4 border-indigo-500'
                      : 'bg-slate-700/30 hover:bg-slate-700/50'
                  } ${!moduleHasVideos ? 'opacity-50' : ''}`}
                  onClick={() => handleModuleSelect(moduleIndex)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold transition-all ${
                      moduleIndex === selectedModule
                        ? 'bg-gradient-to-br from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/50'
                        : !moduleHasVideos ? 'bg-slate-700 text-gray-500' : 'bg-slate-600 text-gray-300'
                    }`}>
                      {module.module_number}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap">
                        <h3 className={`text-sm font-semibold truncate ${
                          moduleIndex === selectedModule ? 'text-white' : !moduleHasVideos ? 'text-gray-500' : 'text-gray-300'
                        }`}>{module.title}</h3>
                        {!moduleHasVideos && <UpdateInProgressBadge />}
                      </div>
                      <p className={`text-xs ${!moduleHasVideos ? 'text-gray-600' : 'text-gray-400'}`}>{module.duration} • {module.lessons.length} lessons</p>
                    </div>
                    <svg
                      className={`w-5 h-5 transition-transform ${
                        moduleIndex === selectedModule ? 'rotate-90' : ''
                      } ${!moduleHasVideos ? 'text-gray-600' : 'text-gray-400'}`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>

                {/* Lessons - Only show for selected module */}
                {moduleIndex === selectedModule && (
                <div className="bg-slate-800/40">
                  {module.lessons.map((lesson: CourseCurriculumLesson, lessonIndex: number) => {
                    const lessonHasVideos = hasLessonVideos(lesson);
                    return (
                    <div key={lesson.id} className="border-b border-slate-700/30 last:border-b-0">
                      <div
                        className={`p-3 pl-6 cursor-pointer transition-colors ${
                          lessonIndex === selectedLesson
                            ? 'bg-indigo-600/10 border-l-4 border-indigo-400'
                            : 'hover:bg-slate-700/40'
                        } ${!lessonHasVideos ? 'opacity-50' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLessonSelect(moduleIndex, lessonIndex);
                        }}
                      >
                        <div className="flex items-start gap-2">
                          <div className={`mt-0.5 w-5 h-5 rounded flex items-center justify-center text-xs border-2 transition-colors ${
                            lessonIndex === selectedLesson
                              ? 'border-indigo-400 bg-indigo-400/20'
                              : !lessonHasVideos ? 'border-slate-700' : 'border-slate-600'
                          }`}>
                            {lessonIndex === selectedLesson && (
                              <svg className="w-3 h-3 text-indigo-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center flex-wrap">
                              <p className={`text-sm font-medium truncate ${
                                lessonIndex === selectedLesson ? 'text-white' : !lessonHasVideos ? 'text-gray-500' : 'text-gray-300'
                              }`}>{lesson.title}</p>
                              {!lessonHasVideos && <UpdateInProgressBadge />}
                            </div>
                            <p className={`text-xs mt-0.5 ${!lessonHasVideos ? 'text-gray-600' : 'text-gray-400'}`}>{lesson.duration} • {lesson.segments.length} segments</p>
                          </div>
                          <svg
                            className={`w-4 h-4 transition-transform flex-shrink-0 ${
                              lessonIndex === selectedLesson ? 'rotate-90' : ''
                            } ${!lessonHasVideos ? 'text-gray-600' : 'text-gray-400'}`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>

                        {/* Segments - Only show for active lesson */}
                        {lessonIndex === selectedLesson && (
                          <div className="mt-3 ml-7 space-y-2">
                            {lesson.segments.map((segment: CourseCurriculumSegment, segmentIndex: number) => {
                              const segmentHasVideos = hasSegmentVideos(segment);
                              return (
                              <div
                                key={segment.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedSegment(segmentIndex);
                                  setSelectedTopic(0);
                                }}
                                className={`p-2.5 rounded-lg cursor-pointer transition-all ${
                                  segmentIndex === selectedSegment
                                    ? 'bg-indigo-600/30 border-2 border-indigo-400 shadow-lg shadow-indigo-500/20'
                                    : 'bg-slate-800/60 hover:bg-slate-700/60 border-2 border-transparent'
                                } ${!segmentHasVideos ? 'opacity-50' : ''}`}
                              >
                                <div className="flex items-center gap-2">
                                  <span className={`flex-shrink-0 ${segmentIndex === selectedSegment ? 'text-indigo-300' : !segmentHasVideos ? 'text-gray-600' : 'text-gray-400'}`}>
                                    {segment.video_url ? (
                                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                                      </svg>
                                    ) : (
                                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                                      </svg>
                                    )}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center flex-wrap gap-1">
                                      <p className={`text-xs font-medium truncate ${
                                        segmentIndex === selectedSegment ? 'text-white' : !segmentHasVideos ? 'text-gray-500' : 'text-gray-300'
                                      }`}>{segment.title}</p>
                                      {!segmentHasVideos && <UpdateInProgressBadge />}
                                    </div>
                                    <p className={`text-xs mt-0.5 ${!segmentHasVideos ? 'text-gray-600' : 'text-gray-400'}`}>{segment.duration} • {segment.topics.length} topics</p>
                                  </div>
                                  {segmentIndex === selectedSegment && (
                                    <svg className="w-4 h-4 text-indigo-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
                )}
              </div>
              );
            })}
          </div>
        )}

        {/* Back to Dashboard */}
        <div className="p-4 border-t border-slate-700">
          {!sidebarCollapsed ? (
            <button
              onClick={() => navigate('/dashboard/courses')}
              className="w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to My Courses
            </button>
          ) : (
            <button
              onClick={() => navigate('/dashboard/courses')}
              className="w-full p-2 hover:bg-slate-700 rounded-lg transition-colors flex items-center justify-center"
            >
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 h-full">
        {/* Top Bar - Current Location */}
        <div className="bg-slate-800/60 backdrop-blur-sm border-b border-slate-700 p-4 flex-shrink-0">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
              <span className="truncate">{currentModule?.title}</span>
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <span className="truncate">{currentLesson?.title}</span>
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
              <span className="truncate">{currentSegment?.title}</span>
            </div>
            <h1 className="text-2xl font-bold text-white truncate">{currentTopic?.content}</h1>
          </div>
        </div>

        {/* Content Display Area */}
        <div className={`flex-1 p-6 min-h-0 ${sidebarCollapsed ? 'overflow-y-auto custom-scrollbar' : 'overflow-hidden flex flex-col'}`}>
          <div className={`max-w-6xl mx-auto w-full ${!sidebarCollapsed ? 'flex-1 flex flex-col min-h-0' : ''}`}>
            <div className={`bg-slate-800/40 backdrop-blur-sm border border-slate-700 rounded-xl shadow-xl ${!sidebarCollapsed ? 'flex-1 flex flex-col min-h-0 max-h-full' : ''}`}>
              {/* Content Type Badge */}
              <div className={`bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-xl ${sidebarCollapsed ? 'p-4' : 'p-5'}`}>
                <div className="flex items-center gap-3 text-white">
                  {currentSegment?.video_url ? (
                    <>
                      <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                      </svg>
                      <span className="text-sm font-semibold uppercase tracking-wide">VIDEO</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm font-semibold uppercase tracking-wide">LESSON</span>
                    </>
                  )}
                  <span className="text-sm opacity-80 flex-shrink-0">• {currentSegment?.duration}</span>
                </div>
              </div>

              {/* Content Body */}
              {/* FULL-SCREEN PLAY CARD - When sidebar expanded, topic has video, and video not expanded */}
              {!sidebarCollapsed && currentTopic && (currentTopic.video_exists || currentTopic.video_url) && courseId && !videoPlayerExpanded ? (
                <button
                  onClick={() => {
                    setSidebarCollapsed(true);
                    setVideoPlayerExpanded(true);
                  }}
                  className="w-full flex-1 rounded-b-xl group relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 cursor-pointer transition-all duration-300 hover:from-slate-800 hover:via-slate-700 hover:to-slate-800"
                >
                  {/* Subtle Grid Pattern */}
                  <div className="absolute inset-0 opacity-[0.02]">
                    <div className="absolute inset-0" style={{
                      backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
                      backgroundSize: '40px 40px'
                    }}></div>
                  </div>

                  {/* Center Content */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    {/* Play Button */}
                    <div className="relative mb-6">
                      <div className="absolute inset-0 w-32 h-32 rounded-full bg-indigo-500/20 blur-2xl group-hover:bg-indigo-500/30 transition-all duration-500"></div>
                      <div className="relative w-32 h-32 rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center shadow-2xl shadow-indigo-500/30 group-hover:scale-110 transition-transform duration-300">
                        <svg className="w-14 h-14 text-white ml-2" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                        </svg>
                      </div>
                    </div>

                    {/* Topic Info */}
                    <div className="text-center px-8">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <span className="text-indigo-400 text-sm font-medium uppercase tracking-wide">Topic {currentTopic.topic_number}</span>
                        {completedTopicIds.has(currentTopic.id) && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs font-medium">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Done
                          </span>
                        )}
                      </div>
                      <h2 className="text-xl font-semibold text-white mb-2">{currentTopic.content}</h2>
                      <p className="text-gray-500 text-sm">Click to play</p>
                    </div>
                  </div>
                </button>
              ) : (
              <div ref={contentAreaRef} className="p-8 overflow-hidden rounded-b-xl">
                {/* Video Player */}
                {currentTopic && (currentTopic.video_exists || currentTopic.video_url) && courseId && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                          <svg className="w-5 h-5 text-indigo-400" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" fillRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-white">Topic {currentTopic.topic_number}</h3>
                          <p className="text-sm text-gray-400">{currentTopic.content}</p>
                        </div>
                      </div>
                      {!sidebarCollapsed && (
                        <button
                          onClick={() => setVideoPlayerExpanded(false)}
                          className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                    <TopicVideoPlayer
                      courseId={courseId}
                      topicId={currentTopic.id}
                      topicContent={currentTopic.content}
                      autoplay={false}
                      enrollmentId={enrollmentId || undefined}
                      segmentId={currentSegment?.id}
                      lessonId={currentLesson?.id}
                      moduleId={currentModule?.id}
                      isAlreadyCompleted={completedTopicIds.has(currentTopic.id)}
                      onTopicCompleted={handleTopicCompleted}
                    />
                  </div>
                )}
              </div>
              )}
            </div>

            {/* Course Resources - Only show when sidebar is collapsed */}
            {sidebarCollapsed && courseId && currentTopic && (
              <CourseResourcesList
                courseId={courseId}
                topicId={currentTopic.id}
              />
            )}

            {/* Course Overview Card - Only show when sidebar is collapsed */}
            {sidebarCollapsed && (
            <div className="mt-6 bg-slate-800/40 backdrop-blur-sm border border-slate-700 rounded-xl p-6 shadow-xl">
              <h3 className={`font-semibold text-white mb-6 flex items-center gap-2 ${sidebarCollapsed ? 'text-xl' : 'text-lg'}`}>
                <svg className={`text-indigo-400 flex-shrink-0 ${sidebarCollapsed ? 'w-6 h-6' : 'w-5 h-5'}`} fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
                </svg>
                Course Overview
              </h3>
              <div className={`grid gap-4 ${sidebarCollapsed ? 'grid-cols-2 xl:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2'}`}>
                {/* Modules Card */}
                <div className={`flex flex-col items-center justify-center bg-slate-700/40 rounded-lg border border-slate-600/30 hover:bg-slate-700/60 transition-all hover:scale-105 min-h-[120px] ${sidebarCollapsed ? 'p-5' : 'p-6'}`}>
                  <div className="flex-1 flex flex-col items-center justify-center w-full">
                    <p className="text-4xl font-bold text-indigo-400 mb-2 tabular-nums">{curriculum.course.overview.total_modules}</p>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider text-center">Modules</p>
                  </div>
                </div>
                {/* Lessons Card */}
                <div className={`flex flex-col items-center justify-center bg-slate-700/40 rounded-lg border border-slate-600/30 hover:bg-slate-700/60 transition-all hover:scale-105 min-h-[120px] ${sidebarCollapsed ? 'p-5' : 'p-6'}`}>
                  <div className="flex-1 flex flex-col items-center justify-center w-full">
                    <p className="text-4xl font-bold text-purple-400 mb-2 tabular-nums">{curriculum.course.overview.total_lessons}</p>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider text-center">Lessons</p>
                  </div>
                </div>
                {/* Segments Card */}
                <div className={`flex flex-col items-center justify-center bg-slate-700/40 rounded-lg border border-slate-600/30 hover:bg-slate-700/60 transition-all hover:scale-105 min-h-[120px] ${sidebarCollapsed ? 'p-5' : 'p-6'}`}>
                  <div className="flex-1 flex flex-col items-center justify-center w-full">
                    <p className="text-4xl font-bold text-pink-400 mb-2 tabular-nums">{curriculum.course.overview.total_segments}</p>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider text-center">Segments</p>
                  </div>
                </div>
                {/* Duration Card */}
                <div className={`flex flex-col items-center justify-center bg-slate-700/40 rounded-lg border border-slate-600/30 hover:bg-slate-700/60 transition-all hover:scale-105 min-h-[120px] ${sidebarCollapsed ? 'p-5' : 'p-6'}`}>
                  <div className="flex-1 flex flex-col items-center justify-center w-full">
                    <p className="text-4xl font-bold text-green-400 mb-2 whitespace-nowrap text-center">{curriculum.course.overview.total_duration}</p>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider text-center">Duration</p>
                  </div>
                </div>
              </div>

              {/* Progress Section */}
              {enrollmentId && (
                <div className="mt-6 pt-6 border-t border-slate-700">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Your Progress
                    </h4>
                    <span className="text-lg font-bold text-green-400">{Math.round(overallProgress)}%</span>
                  </div>
                  <div className="w-full h-3 bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-500"
                      style={{ width: `${overallProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {completedTopicIds.size} topics completed
                    {overallProgress >= 100 && ' - Course Complete!'}
                  </p>
                </div>
              )}
            </div>
            )}
          </div>
        </div>

        {/* Bottom Navigation Bar */}
        <div className="bg-slate-800/60 backdrop-blur-sm border-t border-slate-700 p-3 sm:p-4 flex-shrink-0">
          <div className="max-w-6xl mx-auto flex items-center justify-between gap-2 sm:gap-4">
            {/* Previous Button */}
            <button
              onClick={goToPreviousTopic}
              disabled={isFirstTopic}
              className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition-all flex items-center gap-1 sm:gap-2 flex-shrink-0 ${
                isFirstTopic
                  ? 'bg-slate-700/50 text-gray-500 cursor-not-allowed opacity-50'
                  : 'bg-slate-700 hover:bg-slate-600 text-white hover:shadow-lg hover:scale-105 active:scale-95'
              }`}
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="hidden sm:inline">Previous</span>
            </button>

            {/* Progress Indicator */}
            <div className="text-center flex-1 min-w-0 px-2">
              <p className="text-xs sm:text-sm font-medium text-gray-300 mb-1 sm:mb-2 truncate">
                Topic {selectedTopic + 1} of {currentSegment?.topics.length || 0}
              </p>
              <div className="flex gap-1 sm:gap-1.5 justify-center flex-wrap">
                {currentSegment?.topics.map((_: any, index: number) => (
                  <div
                    key={index}
                    className={`h-1 sm:h-1.5 w-4 sm:w-8 rounded-full transition-all flex-shrink-0 ${
                      index === selectedTopic
                        ? 'bg-indigo-500 shadow-lg shadow-indigo-500/50'
                        : index < selectedTopic
                        ? 'bg-green-500 shadow-md shadow-green-500/30'
                        : 'bg-slate-600'
                    }`}
                  />
                ))}
              </div>
              <p className="text-[10px] sm:text-xs text-gray-500 mt-1 sm:mt-2 font-mono truncate">
                Module {selectedModule + 1}.{selectedLesson + 1}.{selectedSegment + 1}
              </p>
            </div>

            {/* Next Button / Complete */}
            <button
              onClick={goToNextTopic}
              disabled={isLastTopic}
              className={`px-3 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold transition-all flex items-center gap-1 sm:gap-2 flex-shrink-0 ${
                isLastTopic
                  ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white hover:shadow-lg hover:shadow-green-500/50 hover:scale-105 active:scale-95'
                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 hover:shadow-lg hover:shadow-indigo-500/50 text-white hover:scale-105 active:scale-95'
              }`}
            >
              {isLastTopic ? (
                <>
                  <span className="hidden sm:inline">Complete Course</span>
                  <span className="sm:hidden">Done</span>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </>
              ) : (
                <>
                  <span className="hidden sm:inline">Next</span>
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Topic Review Modal */}
      {enrollmentId && courseId && currentTopic && currentSegment && currentLesson && currentModule && (
        <TopicReviewModal
          isOpen={reviewModalOpen}
          onClose={handleReviewModalClose}
          onSubmit={handleReviewSubmitted}
          enrollmentId={enrollmentId}
          courseId={courseId}
          topicId={currentTopic.id}
          segmentId={currentSegment.id}
          lessonId={currentLesson.id}
          moduleId={currentModule.id}
          topicTitle={currentTopic.content}
        />
      )}
    </div>
  );
};

export default Classroom;
